import React, { createContext, useReducer, useContext } from 'react';

const BookingContext = createContext();

const initialState = {
  // Step 1: Search
  checkIn: null, // Date
  checkOut: null, // Date

  // Config (loaded once)
  config: null, // { enterprise, categories, rates, nordicRate, nordicCompanyId, products, serviceId }

  // Step 2: Availability & Room Selection
  availability: null,
  pricing: null,
  roomLines: [], // [{ categoryId, categoryName, quantity, rateId, pricePerNight, flag: 'REQUEST'|'ALLOTMENT', totalPrice }]

  // Step 3: Guest & Booking Details
  poReference: '', // PO#######
  mainGuest: {
    firstName: '',
    lastName: '',
    phone: '',
    nationalityCode: '',
  },
  guests: [], // [{ firstName, lastName, birthDate, isChild }]
  notes: '', // Free-text

  // Step 4: Confirmation
  confirmation: null,

  // UI state
  loading: false,
  error: null,
};

function bookingReducer(state, action) {
  switch (action.type) {
    case 'SET_DATES': {
      // Room-line prices were computed for the previous dates — clear selections
      // when dates change so stale totals can't carry through to confirmation
      const sameDates =
        String(state.checkIn) === String(action.checkIn) &&
        String(state.checkOut) === String(action.checkOut);
      return {
        ...state,
        checkIn: action.checkIn,
        checkOut: action.checkOut,
        roomLines: sameDates ? state.roomLines : [],
      };
    }
    case 'SET_CONFIG':
      return { ...state, config: action.config };
    case 'SET_AVAILABILITY':
      return { ...state, availability: action.availability, pricing: action.pricing };
    case 'SET_ROOM_LINES':
      return { ...state, roomLines: action.roomLines };
    case 'ADD_ROOM_LINE':
      return { ...state, roomLines: [...state.roomLines, action.roomLine] };
    case 'REMOVE_ROOM_LINE':
      return { ...state, roomLines: state.roomLines.filter((_, i) => i !== action.index) };
    case 'UPDATE_ROOM_LINE':
      return {
        ...state,
        roomLines: state.roomLines.map((line, i) =>
          i === action.index ? { ...line, ...action.updates } : line
        ),
      };
    case 'SET_PO_REFERENCE':
      return { ...state, poReference: action.poReference };
    case 'SET_MAIN_GUEST':
      return { ...state, mainGuest: { ...state.mainGuest, ...action.guest } };
    case 'SET_GUESTS':
      return { ...state, guests: action.guests };
    case 'ADD_GUEST':
      return { ...state, guests: [...state.guests, action.guest] };
    case 'REMOVE_GUEST':
      return { ...state, guests: state.guests.filter((_, i) => i !== action.index) };
    case 'UPDATE_GUEST':
      return {
        ...state,
        guests: state.guests.map((g, i) =>
          i === action.index ? { ...g, ...action.updates } : g
        ),
      };
    case 'SET_NOTES':
      return { ...state, notes: action.notes };
    case 'SET_CONFIRMATION':
      return { ...state, confirmation: action.confirmation };
    case 'SET_LOADING':
      return { ...state, loading: action.loading };
    case 'SET_ERROR':
      return { ...state, error: action.error };
    case 'RESET':
      return { ...initialState, config: state.config };
    default:
      return state;
  }
}

export function BookingProvider({ children }) {
  const [state, dispatch] = useReducer(bookingReducer, initialState);

  return (
    <BookingContext.Provider value={{ state, dispatch }}>
      {children}
    </BookingContext.Provider>
  );
}

export function useBooking() {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBooking must be used within a BookingProvider');
  }
  return context;
}

export { BookingContext };
